import React, { useEffect, useState, useRef } from 'react';
import { Box, Heading, VStack, FormControl, FormLabel, Input, FormErrorMessage, Button, useToast, Text, Divider, HStack, Avatar, IconButton, Stack, InputGroup, InputRightElement, Progress, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import client from '../utils/fetchClient';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import { useForm } from 'react-hook-form';
import { useUser } from '../context/UserContext';

export function ProfileTab() {
  const { user, loading, error, updateUser, updateAvatar } = useUser();
  const [submitting, setSubmitting] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const toast = useToast();
  const avatarInputRef = useRef(null);
  // Security form state
  const { register: registerSecurity, handleSubmit: handleSubmitSecurity, formState: { errors: secErrors, isDirty: secIsDirty }, reset: resetSecurity, watch: watchSecurity } = useForm({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const { register, handleSubmit, formState: { errors, isDirty }, reset } = useForm({
    defaultValues: { name: '', password: '' },
  });

  useEffect(() => {
    if (user) {
      reset({ name: user.name || '', password: '' });
    }
  }, [user, reset]);

  const onSubmit = async (values) => {
    if (!user) return;
    setSubmitting(true);
    try {
      const payload = {};
      if (values.name !== user.name) payload.name = values.name;
      if (values.password) payload.password = values.password;
      if (Object.keys(payload).length === 0) {
        toast({ title: 'No changes to save.', status: 'info', duration: 2500, isClosable: true });
        setSubmitting(false);
        return;
      }
      const res = await client.put('/api/users/me', { body: payload });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update profile');
      }
      const updated = await res.json();
      updateUser(updated);
      reset({ name: updated.name || '', password: '' });
      toast({ title: 'Profile updated successfully.', status: 'success', duration: 2500, isClosable: true });
    } catch (e) {
      toast({ title: 'Error updating profile', description: e.message, status: 'error', duration: 3500, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePickAvatar = () => {
    if (avatarInputRef.current) {
      avatarInputRef.current.click();
    }
  };

  function computeStrength(pw) {
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score += 25;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 25;
    if (/\d/.test(pw)) score += 25;
    if (/[^A-Za-z0-9]/.test(pw)) score += 25;
    return Math.min(score, 100);
  }

  const newPasswordValue = watchSecurity('newPassword');
  const strength = computeStrength(newPasswordValue);

  const onChangePassword = async (values) => {
    setChangingPassword(true);
    try {
      if (values.newPassword !== values.confirmPassword) {
        throw new Error('New password and confirmation do not match');
      }
      const res = await client.put('/api/users/me/password', { body: { currentPassword: values.currentPassword, newPassword: values.newPassword } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to change password');
      }
      toast({ title: 'Password updated.', status: 'success', duration: 2500, isClosable: true });
      resetSecurity({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast({ title: 'Error updating password', description: err.message, status: 'error', duration: 3500, isClosable: true });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Please select an image under 5MB.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await client.post('/api/users/me/avatar', { body: formData });
      if (!res.ok) throw new Error('Failed to upload avatar');
      const updated = await res.json();
      updateAvatar(updated.avatarUrl);
      toast({ title: 'Avatar updated.', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Avatar upload failed', description: err.message, status: 'error', duration: 3500, isClosable: true });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.avatarUrl) return;
    setUploadingAvatar(true);
    try {
      const res = await client.delete('/api/users/me/avatar');
      if (!res.ok) throw new Error('Failed to remove avatar');
      updateAvatar('');
      toast({ title: 'Avatar removed.', status: 'info', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Could not remove avatar', description: err.message, status: 'error', duration: 3500, isClosable: true });
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <Box maxW="lg" mx="auto" bg="gray.50" p={8} borderRadius="xl" boxShadow="md" borderWidth={1}>
      <Heading as="h2" size="md" mb={2} color="green.700">Profile</Heading>
      <Text color="gray.500" mb={4}>Update your personal information</Text>
      <Stack direction={{ base: 'column', sm: 'row' }} align={{ base: 'flex-start', sm: 'center' }} spacing={4} mb={6}>
        <Avatar size="xl" name={user?.name} src={user?.avatarUrl} bg="green.200" color="white" />
        <HStack>
          <Button onClick={handlePickAvatar} isLoading={uploadingAvatar} loadingText="Uploading">Change photo</Button>
          <Button variant="ghost" colorScheme="red" onClick={handleRemoveAvatar} isDisabled={!user?.avatarUrl || uploadingAvatar}>Remove</Button>
        </HStack>
        <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarSelected} style={{ display: 'none' }} />
      </Stack>
      <Divider mb={6} />
      {loading ? (
        <LoadingState message="Loading profile" minH="120px" />
      ) : error ? (
        <ErrorState message={error} />
      ) : user ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack align="stretch" spacing={6}>
            <FormControl isInvalid={!!errors.name} isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                {...register('name', { required: 'Name is required' })}
                placeholder="Enter your name"
                autoComplete="name"
                bg="white"
                borderColor="gray.200"
                size="md"
                borderRadius="md"
                fontWeight="medium"
              />
              <FormErrorMessage>{errors.name && errors.name.message}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                value={user.email}
                isReadOnly
                autoComplete="email"
                bg="gray.100"
                borderColor="gray.200"
                size="md"
                borderRadius="md"
                fontWeight="medium"
                color="gray.600"
                _readOnly={{ opacity: 1 }}
              />
            </FormControl>
            <HStack justify="flex-end" mt={2}>
              <Button type="submit" colorScheme="green" isDisabled={!isDirty || submitting} isLoading={submitting} loadingText="Saving" borderRadius="md" px={8}>
                Save Changes
              </Button>
            </HStack>
          </VStack>
        </form>
      ) : null}
      {/* Security Section */}
      <Heading as="h3" size="sm" mt={10} mb={2} color="gray.700">Security</Heading>
      <Text color="gray.500" mb={4}>Update your password and manage two-factor authentication.</Text>
      <Accordion allowToggle>
        <AccordionItem border="none">
          <h2>
            <AccordionButton borderRadius="md" _expanded={{ bg: 'gray.100' }}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">Change Password</Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <form onSubmit={handleSubmitSecurity(onChangePassword)}>
              <VStack align="stretch" spacing={5}>
                <FormControl isInvalid={!!secErrors.currentPassword} isRequired>
                  <FormLabel>Current Password</FormLabel>
                  <InputGroup>
                    <Input type={showCurrent ? 'text' : 'password'} placeholder="Enter current password" autoComplete="current-password" bg="white" borderColor="gray.200" {...registerSecurity('currentPassword', { required: 'Current password is required' })} />
                    <InputRightElement>
                      <IconButton aria-label={showCurrent ? 'Hide password' : 'Show password'} size="sm" variant="ghost" icon={showCurrent ? <ViewOffIcon /> : <ViewIcon />} onClick={() => setShowCurrent(v => !v)} />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{secErrors.currentPassword && secErrors.currentPassword.message}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!secErrors.newPassword} isRequired>
                  <FormLabel>New Password</FormLabel>
                  <InputGroup>
                    <Input type={showNew ? 'text' : 'password'} placeholder="Enter new password" autoComplete="new-password" bg="white" borderColor="gray.200" {...registerSecurity('newPassword', { required: 'New password is required', minLength: { value: 8, message: 'Must be at least 8 characters' } })} />
                    <InputRightElement>
                      <IconButton aria-label={showNew ? 'Hide password' : 'Show password'} size="sm" variant="ghost" icon={showNew ? <ViewOffIcon /> : <ViewIcon />} onClick={() => setShowNew(v => !v)} />
                    </InputRightElement>
                  </InputGroup>
                  <Progress value={strength} size="sm" mt={2} colorScheme={strength < 50 ? 'red' : strength < 75 ? 'yellow' : 'green'} borderRadius="md" />
                  <FormErrorMessage>{secErrors.newPassword && secErrors.newPassword.message}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!secErrors.confirmPassword} isRequired>
                  <FormLabel>Confirm New Password</FormLabel>
                  <InputGroup>
                    <Input type={showConfirm ? 'text' : 'password'} placeholder="Re-enter new password" autoComplete="new-password" bg="white" borderColor="gray.200" {...registerSecurity('confirmPassword', { required: 'Please confirm your new password' })} />
                    <InputRightElement>
                      <IconButton aria-label={showConfirm ? 'Hide password' : 'Show password'} size="sm" variant="ghost" icon={showConfirm ? <ViewOffIcon /> : <ViewIcon />} onClick={() => setShowConfirm(v => !v)} />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{secErrors.confirmPassword && secErrors.confirmPassword.message}</FormErrorMessage>
                </FormControl>
              </VStack>
              <Box position={{ base: 'sticky', md: 'static' }} bottom={0} bg={{ base: 'gray.50', md: 'transparent' }} pt={4} mt={6} borderTop={{ base: '1px solid', md: '0' }} borderColor="gray.200">
                <HStack justify="flex-end">
                  <Button variant="ghost" onClick={() => resetSecurity({ currentPassword: '', newPassword: '', confirmPassword: '' })}>Cancel</Button>
                  <Button colorScheme="green" type="submit" isLoading={changingPassword} isDisabled={!secIsDirty || changingPassword}>Update Password</Button>
                </HStack>
              </Box>
            </form>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem border="none">
          <h2>
            <AccordionButton borderRadius="md" _expanded={{ bg: 'gray.100' }}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">Two-Factor Authentication (2FA)</Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <Text color="gray.600" mb={3}>Protect your account with an extra layer of security. Set up 2FA to require a code when signing in.</Text>
            <HStack>
              <Button colorScheme="blue" variant="solid">Set up 2FA</Button>
              <Button variant="ghost">Learn more</Button>
            </HStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}

export default ProfileTab;